// Часть разбиения MapView.vue (фаза 2) - отладочная панель для подбора
// стилей базовой карты (зум + атрибуты объекта по клику). Специально без
// vue-i18n - см. пояснение у debugPanelOpen в исходном MapView.vue.
import { ref } from "vue";
import type * as maplibregl from "maplibre-gl";
import type { MapCtx } from "../map/mapContext";

export const safeStringify = (value: any) => {
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export function useMapDebugPanel(ctx: MapCtx) {
  const debugPanelOpen = ref(false);
  const debugZoom = ref(0);
  // Центр карты «широта, долгота» - чтобы можно было назвать место из панели.
  const debugCenter = ref("");
  const debugFeatures = ref<{ layer: string; properties: Record<string, unknown> }[]>([]);

  const pushDebug = (...args: unknown[]) => {
    const message = args.map((arg) => safeStringify(arg)).join(" ");
    const line = `${new Date().toLocaleTimeString()} ${message}`;
    (window as any).__mapViewLogs = [
      ...(((window as any).__mapViewLogs as string[] | undefined) || []).slice(-4),
      line,
    ];
  };

  const updateDebugZoom = () => {
    const map = ctx.getMap();
    if (!map) return;
    debugZoom.value = map.getZoom();
    const c = map.getCenter();
    debugCenter.value = `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
  };

  // queryRenderedFeatures отдаёт по одному попаданию на СЛОЙ в этой точке, а не
  // на реальный объект - halo/label/icon-слои одного POI иначе давали бы
  // несколько почти одинаковых строк. Пропуск id.endsWith('-halo') и
  // дедупликация по id слоя оставляют одну строку на визуально отдельную вещь.
  const handleDebugClick = (e: maplibregl.MapMouseEvent) => {
    const map = ctx.getMap();
    if (!map || !debugPanelOpen.value) return;
    const seen = new Set<string>();
    debugFeatures.value = map
      .queryRenderedFeatures(e.point)
      .filter((f) => {
        const id = f.layer?.id ?? "";
        if (!id || id === "basemap-background" || id.endsWith("-halo") || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, 10)
      .map((f) => ({ layer: f.layer!.id, properties: { ...(f.properties ?? {}) } }));
  };

  const formatDebugProps = (props: Record<string, unknown>): string => {
    const entries = Object.entries(props).filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (entries.length === 0) return "(нет свойств)";
    return entries.map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`).join("\n");
  };

  return {
    debugPanelOpen,
    debugZoom,
    debugCenter,
    debugFeatures,
    pushDebug,
    updateDebugZoom,
    handleDebugClick,
    formatDebugProps,
  };
}
